import React, { lazy, Suspense, useEffect, useState } from 'react';

import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { setIpData, setLocale } from '@/store/actions/commonActions';
import { selectIsFinOrSv, selectIsThailand, selectLocale } from '@/store/selectors/commonSelectors';
import { getLocaleByNavigator, localeFromPath } from '@/utils/utils';
import { mapLocaleToPathLocale } from '@/utils/mappers';
import { logException } from '@/utils/logger';
import { getLocaleByCountryCode, isRegulatedLocale } from '@/shared/utility';
import { buildLocales } from '@/constants/locales';
import {
  envIsCanada,
  envIsMexico,
  envIsNormal,
  envIsSweden,
  IS_HALLOWEEN,
} from '@/constants/constants';
import {
  isMarketClosureActive,
  MARKET_CLOSURE_OPEN_LOCALES,
  MarketClosurePage,
} from '@/features/market-closure/MarketClosurePage';
import { fetchIpData } from '@/utils/ip-data.utils';
import GamesByProvider from '@/pages/games/categories/GamesByProvider';
import { gamesFeaturesList } from '@/utils/logic.utils';
import Internationalisation from '@/features/internationalization/Internationalisation';
import Home from '@/pages/home/Home';
import Games from '@/pages/games/Games';
import GamesLobby from '@/pages/games/lobby/GamesLobby';
import { NotFound } from '@/components/Pages/NotFound';
import Game from '@/pages/games/game/Game';
import Help from '@/components/Pages/Help/Help';
import AboutUs from '@/pages/about-us/AboutUs';
import ResetPassword from '@/features/authentication/reset-password/ResetPassword';
import { Impersonate } from '@/components/Pages/Impersonate';
import PrivacyPolicy from '@/components/Pages/PrivacyPolicy';
import TermsAndConditions from '@/components/Pages/TermsAndConditions';
import { PrivateRoutes } from '@/components/PrivateRoutes';
import SettingsHeader from '@/pages/settings/SettingsHeader';
import Details from '@/pages/settings/details/Details';
import Verify from '@/pages/settings/verify/Verify';
import GamingHistory from '@/pages/settings/history/GamingHistory';
import PaymentsHistory from '@/pages/settings/history/PaymentsHistory';
import { LoginHistory } from '@/components/LoginHistory';
import ActiveBonuses from '@/pages/settings/active-bonuses/ActiveBonuses';
import Logout from '@/components/Pages/Logout';
import History from '@/pages/settings/history/History';
import App from '@/features/app/App';
import { BffGamesCategory } from '@lucky7ventures/bff-types';
import RewardsWheelPage from '@/pages/rewards-wheel/RewardsWheelPage';
import { useRewardsWheel } from '@/hooks/useRewardsWheel';
import ResponsibleGamingPage from '@/pages/responsible-gaming/ResponsibleGamingPage';
import ResponsibleGamingTab from '@/pages/settings/responsible-gaming/ResponsibleGamingTab';
import PromotionsPage from '@/pages/promotions/PromotionsPage';
import PromotionPage from '@/pages/promotion/PromotionPage';
import ForgotPassword from '@/pages/forgot-password/ForgotPassword';
import ForgotPasswordSms from '@/pages/forgot-password-sms/ForgotPasswordSms';
import GamesByProviderCategory from '@/pages/games/providers/GamesByProviderCategory';
import Providers from '@/pages/games/providers/Providers';
import { ADDITIONAL_GAME_CATEGORIES, SHARED_GAME_CATEGORIES } from '@/constants/games-constants';
import CategoryPage from '@/pages/games/categories/CategoryPage';
import CategoryLiveCasinoGames from '@/pages/games/categories/CategoryLiveCasinoGames';
import BonusTermsAndConditions from '@/components/Pages/BonusTermsAndConditions';
const PaymentMethods = lazy(() => import('@/pages/payment-providers/PaymentMethods'));

export function AppRouter(): JSX.Element | null {
  const locale = useSelector(selectLocale);
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isNormalNotRegulated = !useSelector(selectIsFinOrSv);
  const isThailand = useSelector(selectIsThailand);
  const [finalPath, setFinalPath] = useState<string | undefined>();
  const { isEnabled: isRewardsReelEnabled, pagePath: rewardsWheelPagePath } = useRewardsWheel();
  const shouldShowExpandedLiveCasino =
    (isNormalNotRegulated || envIsCanada()) && !isThailand && !envIsMexico();

  useEffect(() => {
    if (envIsMexico()) {
      dispatch(setLocale('mx'));
      return;
    }

    if (envIsSweden()) {
      dispatch(setLocale('sv'));
      return;
    }

    if (envIsCanada()) {
      const localeByNavigator = getLocaleByNavigator();
      const pathLocale = localeFromPath(location);

      if (pathLocale) {
        dispatch(setLocale('ca-fr'));
        return;
      }

      dispatch(setLocale(localeByNavigator));
      return;
    }

    const pathLocale = localeFromPath(location);

    if (pathLocale) {
      // Locale is included in the url set locale in redux storage
      dispatch(setLocale(pathLocale));
    } else {
      // Locale is not included in the url -> get country code by IP and set locale in redux storage
      fetchIpData()
        .then(data => {
          const countryCode = data.country;
          let ipDataLocale = getLocaleByCountryCode(countryCode);

          // Check if the user should be redirected to /ca or /ca-fr when coming from a canadian IP
          if (countryCode === 'CA') {
            ipDataLocale = getLocaleByNavigator();
          }
          dispatch(setIpData(data));
          dispatch(setLocale(ipDataLocale));
        })
        .catch(e => {
          logException(new Error('App Routes Ip Data Error'), { error: e });
          // Fallback to /row if IP API Fails
          dispatch(setLocale('row'));
        });
    }
  }, []);

  useEffect(() => {
    if (!locale) {
      return;
    }
    // Check if the pathname starts with at least one of the locales
    // if not append the locale to the pathname
    const pathname = location.pathname;
    const pathLocale = localeFromPath(location);

    if (envIsCanada()) {
      if (locale === 'ca') {
        setFinalPath(pathname);
        return;
      }
    }

    // if the locale already starts with one of the LOCALES set it as a final path
    // also for Sweden and Mexico build we want to keep the path as it is
    if (envIsSweden() || envIsMexico() || pathLocale) {
      setFinalPath(pathname);
      return;
    }

    // in other cases, if the route doesn't start with locale we need to do a redirect to the locale
    const path = pathname === '/' ? '' : pathname;
    const combinedPath = `${mapLocaleToPathLocale(locale)}${path}${location.search}`;
    setFinalPath(combinedPath);
    navigate(combinedPath);
  }, [locale]);

  if (!finalPath) {
    return null;
  }

  // LP-1281: .com sales closure - every market except Finland shows the static closure page.
  if (envIsNormal() && isMarketClosureActive() && !MARKET_CLOSURE_OPEN_LOCALES.includes(locale)) {
    return <MarketClosurePage locale={locale} />;
  }

  return (
    <Internationalisation locale={locale}>
      <Suspense fallback={<div />}>
        <Routes>
          {buildLocales().map(loc => (
            <Route key={loc} path={loc} element={<App />}>
              <Route index element={<Home />} />
              <Route path={`games/provider/:provider`} element={<GamesByProvider />} />
              <Route path={`games/:provider/cat/:category`} element={<GamesByProviderCategory />} />
              <Route path="games" element={<Games />}>
                <Route index element={<GamesLobby />} />
                {SHARED_GAME_CATEGORIES.map(sharedCategory => (
                  <Route
                    key={`route-shared-category-${sharedCategory}`}
                    path={sharedCategory}
                    element={<CategoryPage category={sharedCategory} subheader />}
                  />
                ))}
                {ADDITIONAL_GAME_CATEGORIES.map(additionalCategory => (
                  <Route
                    key={`route-additional-category-${additionalCategory}`}
                    path={additionalCategory}
                    element={<CategoryPage category={additionalCategory} subheader />}
                  />
                ))}
                {/*For now exclusive to Thailand locale*/}
                {locale === 'th' && <Route path="providers" element={<Providers />} />}
                {IS_HALLOWEEN && (
                  <Route
                    path={BffGamesCategory.HalloweenGames}
                    element={<CategoryPage category={BffGamesCategory.HalloweenGames} subheader />}
                  />
                )}

                {!shouldShowExpandedLiveCasino && (
                  <Route
                    path={BffGamesCategory.LiveCasino}
                    element={<CategoryPage category={BffGamesCategory.LiveCasino} subheader />}
                  />
                )}
                {shouldShowExpandedLiveCasino && (
                  <Route path={BffGamesCategory.LiveCasino} element={<CategoryLiveCasinoGames />} />
                )}
                {gamesFeaturesList(locale).map(feature => (
                  <Route
                    key={`route-${feature}`}
                    path={feature}
                    element={<CategoryPage category={feature} />}
                  />
                ))}
                <Route element={<NotFound />} />
              </Route>
              <Route path="games/:game/:gameId" element={<Game />} />
              {locale !== 'eu' && <Route path="help" element={<Help />} />}
              {envIsMexico() && <Route path="payment-methods" element={<PaymentMethods />} />}
              {envIsMexico() && <Route path="about-us" element={<AboutUs />} />}
              {!isRegulatedLocale(locale) && (
                <Route path="forgot-password" element={<ForgotPassword />} />
              )}
              {locale === 'th' && (
                <Route path="forgot-password-sms" element={<ForgotPasswordSms />} />
              )}
              <Route path="reset-password/:recoveryCode" element={<ResetPassword />} />
              <Route path="impersonate/:token" element={<Impersonate />} />
              <Route path="responsible-gaming" element={<ResponsibleGamingPage />} />
              <Route path="privacy-policy" element={<PrivacyPolicy />} />
              <Route path="terms-and-conditions" element={<TermsAndConditions />} />
              <Route path="bonus-terms-and-conditions" element={<BonusTermsAndConditions />} />
              <Route path="cashier" element={<Home cashierDrawerOpen={true} />} />
              <Route path="promotions" element={<PromotionsPage />} />
              <Route path="promotion/:slug" element={<PromotionPage />} />
              {isRewardsReelEnabled && (
                <Route path={rewardsWheelPagePath} element={<RewardsWheelPage />} />
              )}
              {/* Private Routes */}
              <Route element={<PrivateRoutes />}>
                <Route path="settings" element={<SettingsHeader />}>
                  <Route index element={<Details />} />
                  <Route path="verify" element={<Verify />} />
                  <Route path="responsible-gaming" element={<ResponsibleGamingTab />} />
                  <Route path="history" element={<History />}>
                    <Route index element={<GamingHistory />} />
                    <Route path="payments" element={<PaymentsHistory />} />
                    {(envIsSweden() || envIsCanada()) && (
                      <Route path="login" element={<LoginHistory />} />
                    )}
                  </Route>
                  <Route path="active-bonuses" element={<ActiveBonuses />} />
                </Route>
                <Route path="logout" element={<Logout />} />
              </Route>
              <Route path="*" element={<NotFound />} />
            </Route>
          ))}
          <Route path="*" element={<Navigate to={locale} />} />
        </Routes>
      </Suspense>
    </Internationalisation>
  );
}
